// =============================================================================
// screens/Placeholder.tsx — the shared premium "coming soon" surface.
//
// Originally a bare P1 stub (a translucent card that literally said
// "placeholder — ported in P3"). It is now an intentional, on-brand glass
// screen: a gradient v2 glyph, headline, supporting copy and a clear
// ComingSoonBadge, all built on the design-system tokens (GlassCard / glass
// depth / typographyScale / shadowTokens / colorTokens). It reads as a
// deliberate "this lands in CRYB v2" moment for any not-yet-ported route rather
// than a debug placeholder — no bare voids, no hardcoded rgba/hex/fontSize.
//
// Responsive: the content column is width-capped and centred on tablet/desktop
// via Screen's constrainWidth, and the hero card itself caps its own width so it
// never stretches edge-to-edge on a wide canvas.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../components/ui/Icon';
import Screen from '../components/ui/Screen';
import { GlassCard } from '../components/glass';
import ComingSoonBadge from '../components/ComingSoonBadge';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  shadowTokens,
  colorTokens,
  getLayout,
} from '../constants/theme';

export function makePlaceholder(label: string) {
  function PlaceholderScreen() {
    const { width } = useWindowDimensions();
    const isCompact = getLayout(width) === 'phone';

    return (
      <Screen edges={['top']} scroll contentContainerStyle={styles.content}>
        <View style={styles.stage}>
          <GlassCard style={styles.card} contentStyle={styles.cardContent}>
            {/* Gradient v2 glyph — the anchor that makes the card feel intentional. */}
            <LinearGradient
              colors={colors.gradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[styles.glyph, isCompact ? styles.glyphCompact : null]}
            >
              <Ionicons name="sparkles" size={isCompact ? 26 : 30} color={colors.white} />
            </LinearGradient>

            <ComingSoonBadge label="CRYB v2" />

            <Text style={styles.title}>{label}</Text>
            <Text style={styles.sub}>
              We're crafting something worth the wait. {label} arrives in CRYB v2 —
              polished, fast and unmistakably CRYB.
            </Text>

            {/* Subtle "in progress" strip — depth without a dead void. */}
            <View style={styles.progressTrack}>
              <LinearGradient
                colors={colors.gradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.progressFill}
              />
            </View>
            <Text style={styles.progressLabel}>In active development</Text>
          </GlassCard>
        </View>
      </Screen>
    );
  }
  PlaceholderScreen.displayName = `Placeholder(${label})`;
  return PlaceholderScreen;
}

const styles = StyleSheet.create({
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xxxl,
  },
  stage: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    ...shadowTokens.card,
  },
  cardContent: {
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.xxxl,
    paddingHorizontal: spacing.xl,
  },
  glyph: {
    width: 68,
    height: 68,
    borderRadius: radius.xl,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadowTokens.bar,
    shadowColor: colors.primary,
    marginBottom: spacing.xs,
  },
  glyphCompact: {
    width: 60,
    height: 60,
  },
  title: {
    ...typographyScale.h2,
    ...fonts.extrabold,
    color: colorTokens.light.text,
    textAlign: 'center',
  },
  sub: {
    ...typographyScale.body,
    color: colorTokens.light.textMuted,
    textAlign: 'center',
    paddingHorizontal: spacing.sm,
  },
  progressTrack: {
    width: '70%',
    height: 6,
    borderRadius: radius.full,
    backgroundColor: 'rgba(108,92,231,0.12)',
    overflow: 'hidden',
    marginTop: spacing.sm,
  },
  progressFill: {
    width: '55%',
    height: '100%',
    borderRadius: radius.full,
  },
  progressLabel: {
    ...typographyScale.caption,
    ...fonts.semibold,
    color: colorTokens.light.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
});
